import React from 'react';
import { Link } from 'react-router-dom';
import { User, Users, Briefcase, Building2, UserCheck, ChevronRight } from 'lucide-react';
import { UserProfile } from '../types/userProfile';
import { ROUTES } from '../config/routes';

interface EmployeeInfoBannerProps {
  userProfile: UserProfile | null;
  loading?: boolean;
}

export function EmployeeInfoBanner({ userProfile, loading = false }: EmployeeInfoBannerProps) {
  if (loading) {
    return (
      <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl p-8 mb-8 animate-pulse shadow-xl">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/20"></div>
          <div className="flex-1">
            <div className="h-6 bg-white/20 rounded w-48 mb-2"></div>
            <div className="h-4 bg-white/20 rounded w-32"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return null;
  }

  const directReportsCount = userProfile.directReports?.length || 0;
  const hasDirectReports = directReportsCount > 0;

  return (
    <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl p-8 mb-8 shadow-xl text-white">
      <div className="flex flex-col lg:flex-row lg:items-center gap-6">
        {/* User Avatar and Name */}
        <div className="flex items-center gap-4 flex-1">
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center shadow-lg ring-4 ring-white/10 overflow-hidden backdrop-blur-sm">
            {userProfile.photoUrl ? (
              <img 
                src={userProfile.photoUrl} 
                alt={userProfile.displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <User className="w-7 h-7 text-white" />
            )}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {userProfile.displayName}
            </h1>
            <p className="text-white/60 text-sm">
              {userProfile.employeeId}
            </p>
          </div>
        </div>

        {/* Info Cards */}
        <div className="flex flex-wrap gap-3">
          {/* Job Title */}
          <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/10">
            <Briefcase className="w-4 h-4 text-white/70" />
            <span className="text-white/90 text-sm">{userProfile.jobTitle || 'N/A'}</span>
          </div>

          {/* Department */}
          <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/10">
            <Building2 className="w-4 h-4 text-white/70" />
            <span className="text-white/90 text-sm">{userProfile.department || 'N/A'}</span>
          </div>

          {/* Manager */}
          {userProfile.manager && (
            <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/10">
              <UserCheck className="w-4 h-4 text-white/70" />
              <span className="text-white/80 text-sm">
                Manager: <span className="text-white font-medium">{userProfile.manager.displayName}</span>
              </span>
            </div>
          )}

          {/* Direct Reports */}
          <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/10">
            <Users className="w-4 h-4 text-white/70" />
            <span className="text-white/80 text-sm">
              <span className="text-white font-semibold">{directReportsCount}</span> Direct Report{directReportsCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
